import React, { useState } from 'react';
import { Calendar, Plus, Trash2, Check, X, Pencil, List, ChevronLeft, ChevronRight } from 'lucide-react';
import Button from '../ui/Button';

export interface AvailabilityPeriod {
  id?: string;
  startDate: string; // ISO8601Date format (YYYY-MM-DD)
  endDate?: string; // Optional for open-ended availability
  available: boolean;
}

interface ToolAvailabilityCalendarProps {
  periods: AvailabilityPeriod[];
  onChange: (periods: AvailabilityPeriod[]) => void;
  toolId?: string; // For existing tools
  readOnly?: boolean; // For read-only view
}

const ToolAvailabilityCalendar: React.FC<ToolAvailabilityCalendarProps> = ({
  periods,
  onChange,
  readOnly = false,
}) => {
  const [isAddingPeriod, setIsAddingPeriod] = useState(false);
  const [newPeriod, setNewPeriod] = useState<AvailabilityPeriod>({
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    available: true,
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editPeriod, setEditPeriod] = useState<AvailabilityPeriod | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>(readOnly ? 'calendar' : 'list');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDateRange, setSelectedDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [showPeriodDialog, setShowPeriodDialog] = useState(false);
  const [dialogPeriodType, setDialogPeriodType] = useState<'available' | 'unavailable'>('available');
  const [selectedDayPeriods, setSelectedDayPeriods] = useState<{ date: Date; dateStr: string; periods: Array<{ period: AvailabilityPeriod; index: number }> } | null>(null);
  const [calendarEditingIndex, setCalendarEditingIndex] = useState<number | null>(null);
  const [calendarEditPeriod, setCalendarEditPeriod] = useState<AvailabilityPeriod | null>(null);

  const handleAddPeriod = () => {
    if (!newPeriod.startDate) return;

    // Validate dates
    if (newPeriod.endDate && newPeriod.endDate < newPeriod.startDate) {
      alert('Slutdato skal være efter startdato');
      return;
    }

    onChange([...periods, newPeriod]);
    setNewPeriod({
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      available: true,
    });
    setIsAddingPeriod(false);
  };

  const handleDeletePeriod = (index: number) => {
    onChange(periods.filter((_, i) => i !== index));
  };

  const handleStartEdit = (index: number) => {
    setEditingIndex(index);
    setEditPeriod({ ...periods[index] });
  };

  const handleSaveEdit = () => {
    if (editingIndex === null || !editPeriod) return;

    // Validate dates
    if (editPeriod.endDate && editPeriod.endDate < editPeriod.startDate) {
      alert('Slutdato skal være efter startdato');
      return;
    }

    const updatedPeriods = [...periods];
    updatedPeriods[editingIndex] = editPeriod;
    onChange(updatedPeriods);
    setEditingIndex(null);
    setEditPeriod(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditPeriod(null);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Calendar view helpers
  const getAvailabilityForDate = (dateStr: string): 'available' | 'unavailable' | 'none' => {
    // Find all matching periods for this date
    const matchingPeriods = periods.filter(period => {
      const startDateStr = period.startDate;
      const endDateStr = period.endDate;
      
      return dateStr >= startDateStr && (!endDateStr || dateStr <= endDateStr);
    });
    
    if (matchingPeriods.length === 0) {
      return 'none';
    }
    
    // Prioritize periods with end dates (more specific) over open-ended ones
    // If multiple specific periods, use the last one
    const specificPeriods = matchingPeriods.filter(p => p.endDate);
    const periodToUse = specificPeriods.length > 0 
      ? specificPeriods[specificPeriods.length - 1]
      : matchingPeriods[matchingPeriods.length - 1];
    
    return periodToUse.available ? 'available' : 'unavailable';
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startDayOfWeek, year, month };
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDayClick = (date: Date, dateStr: string) => {
    if (readOnly) return; // Disable interaction in read-only mode
    
    // Find all periods that affect this date
    const affectedPeriods = periods
      .map((period, index) => ({ period, index }))
      .filter(({ period }) => {
        const startDateStr = period.startDate;
        const endDateStr = period.endDate;
        return dateStr >= startDateStr && (!endDateStr || dateStr <= endDateStr);
      });

    if (affectedPeriods.length > 0) {
      // Show existing periods for this day
      setSelectedDayPeriods({ date, dateStr, periods: affectedPeriods });
      setSelectedDateRange({ start: null, end: null });
    } else {
      // Start new period selection
      if (!selectedDateRange.start) {
        setSelectedDateRange({ start: date, end: null });
      } else if (!selectedDateRange.end) {
        const start = selectedDateRange.start;
        const end = date;
        if (end >= start) {
          setSelectedDateRange({ start, end });
          setShowPeriodDialog(true);
        } else {
          // If clicked earlier date, restart selection
          setSelectedDateRange({ start: date, end: null });
        }
      } else {
        // Reset and start new selection
        setSelectedDateRange({ start: date, end: null });
      }
    }
  };

  const handleDayDoubleClick = (date: Date, dateStr: string) => {
    if (readOnly) return; // Disable interaction in read-only mode
    
    // Find all periods that affect this date
    const affectedPeriods = periods
      .map((period, index) => ({ period, index }))
      .filter(({ period }) => {
        const startDateStr = period.startDate;
        const endDateStr = period.endDate;
        return dateStr >= startDateStr && (!endDateStr || dateStr <= endDateStr);
      });

    if (affectedPeriods.length === 0) {
      // Open dialog with same date as start and end
      setSelectedDateRange({ start: date, end: date });
      setShowPeriodDialog(true);
    }
  };

  const handleOpenNewPeriodDialog = () => {
    const today = new Date();
    setSelectedDateRange({ start: today, end: null });
    setDialogPeriodType('available');
    setShowPeriodDialog(true);
  };

  const handleCreatePeriodFromCalendar = () => {
    if (!selectedDateRange.start) return;

    const newPeriod: AvailabilityPeriod = {
      startDate: selectedDateRange.start.toISOString().split('T')[0],
      endDate: selectedDateRange.end ? selectedDateRange.end.toISOString().split('T')[0] : undefined,
      available: dialogPeriodType === 'available',
    };

    onChange([...periods, newPeriod]);
    setSelectedDateRange({ start: null, end: null });
    setShowPeriodDialog(false);
    setDialogPeriodType('available');
  };

  const handleDeletePeriodFromCalendar = (index: number) => {
    onChange(periods.filter((_, i) => i !== index));
    setSelectedDayPeriods(null);
  };

  const handleStartEditFromCalendar = (index: number) => {
    setCalendarEditingIndex(index);
    setCalendarEditPeriod({ ...periods[index] });
  };

  const handleSaveEditFromCalendar = () => {
    if (calendarEditingIndex === null || !calendarEditPeriod) return;

    // Validate dates
    if (calendarEditPeriod.endDate && calendarEditPeriod.endDate < calendarEditPeriod.startDate) {
      alert('Slutdato skal være efter startdato');
      return;
    }

    const updatedPeriods = [...periods];
    updatedPeriods[calendarEditingIndex] = calendarEditPeriod;
    onChange(updatedPeriods);
    setCalendarEditingIndex(null);
    setCalendarEditPeriod(null);
    setSelectedDayPeriods(null);
  };

  const handleCancelEditFromCalendar = () => {
    setCalendarEditingIndex(null);
    setCalendarEditPeriod(null);
  };

  const isDateInRange = (date: Date): boolean => {
    if (!selectedDateRange.start) return false;
    if (!selectedDateRange.end) return date.toDateString() === selectedDateRange.start.toDateString();
    return date >= selectedDateRange.start && date <= selectedDateRange.end;
  };

  const renderCalendarView = () => {
    const { daysInMonth, startDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const days = [];
    const monthName = currentMonth.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' });
    
    // Add empty cells for days before the first day of the month
    const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Monday = 0
    for (let i = 0; i < adjustedStartDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square" />);
    }
    
    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      // Create date string directly to avoid timezone issues
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const availability = getAvailabilityForDate(dateStr);
      const isToday = new Date().toDateString() === date.toDateString();
      const isInSelection = isDateInRange(date);
      const isPast = dateStr < new Date().toISOString().split('T')[0];
      
      let bgColor = 'bg-white hover:bg-gray-50';
      if (isPast) {
        bgColor = 'bg-gray-100 cursor-not-allowed opacity-50';
      } else if (isInSelection) {
        bgColor = 'bg-blue-200 hover:bg-blue-300 border-blue-400';
      } else if (availability === 'available') {
        bgColor = 'bg-green-100 hover:bg-green-200 border-green-300';
      } else if (availability === 'unavailable') {
        bgColor = 'bg-red-100 hover:bg-red-200 border-red-300';
      }
      
      days.push(
        <button
          type="button"
          key={day}
          onClick={() => !isPast && handleDayClick(date, dateStr)}
          onDoubleClick={() => !isPast && handleDayDoubleClick(date, dateStr)}
          disabled={isPast}
          className={`aspect-square border rounded-lg p-1 flex flex-col items-center justify-center transition-colors ${
            isPast ? 'cursor-not-allowed' : 'cursor-pointer'
          } ${bgColor} ${
            isToday ? 'ring-2 ring-blue-500' : ''
          }`}
        >
          <span className={`text-sm ${isToday ? 'font-bold' : ''}`}>{day}</span>
        </button>
      );
    }
    
    return (
      <div className="space-y-4">
        {/* Calendar Header */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={goToPreviousMonth}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h3 className="text-lg font-semibold capitalize">{monthName}</h3>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <button
                type="button"
                onClick={handleOpenNewPeriodDialog}
                className="p-2 hover:bg-green-100 text-green-600 rounded-lg transition-colors"
                title="Tilføj periode"
              >
                <Plus className="h-5 w-5" />
              </button>
            )}
            <button
              type="button"
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Instructions */}
        {!readOnly && (
          <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
            {selectedDateRange.start && !selectedDateRange.end ? (
              <p>Klik på slutdato for at oprette periode...</p>
            ) : (
              <p>Klik på en dag med periode for at se detaljer, eller klik på en tom dag for at starte ny periode</p>
            )}
          </div>
        )}
        
        {/* Day labels */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map((day) => (
            <div key={day} className="text-center text-sm font-medium text-gray-600">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-2">
          {days}
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap gap-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-100 border border-green-300 rounded" />
            <span className="text-sm text-gray-600">Tilgængelig</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-100 border border-red-300 rounded" />
            <span className="text-sm text-gray-600">Ikke tilgængelig</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-white border border-gray-300 rounded" />
            <span className="text-sm text-gray-600">Ingen information</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-green-600" />
            Tilgængelighed
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {readOnly 
              ? 'Se hvornår værktøjet er tilgængeligt til udlån'
              : 'Angiv hvornår dit værktøj er tilgængeligt til udlån'
            }
          </p>
        </div>
        
        {/* View Toggle */}
        {!readOnly && (
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Calendar className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          {renderCalendarView()}
          
          {/* Period Creation Dialog */}
          {showPeriodDialog && selectedDateRange.start && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowPeriodDialog(false)}>
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold mb-4">Opret ny periode</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={dialogPeriodType === 'available'}
                          onChange={() => setDialogPeriodType('available')}
                          className="text-green-600"
                        />
                        <span className="text-sm">Tilgængelig</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={dialogPeriodType === 'unavailable'}
                          onChange={() => setDialogPeriodType('unavailable')}
                          className="text-red-600"
                        />
                        <span className="text-sm">Ikke tilgængelig</span>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fra dato <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={selectedDateRange.start.toISOString().split('T')[0]}
                        onChange={(e) => {
                          const newDate = new Date(e.target.value);
                          setSelectedDateRange({ ...selectedDateRange, start: newDate });
                        }}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Til dato (valgfrit)
                      </label>
                      <input
                        type="date"
                        value={selectedDateRange.end ? selectedDateRange.end.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                          const newDate = e.target.value ? new Date(e.target.value) : null;
                          setSelectedDateRange({ ...selectedDateRange, end: newDate });
                        }}
                        min={selectedDateRange.start ? selectedDateRange.start.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Ubegrænset"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Lad feltet være tomt for ubegrænset tilgængelighed
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button
                      type="button"
                      onClick={handleCreatePeriodFromCalendar}
                      variant="primary"
                      className="flex-1"
                    >
                      Opret periode
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setShowPeriodDialog(false);
                        setSelectedDateRange({ start: null, end: null });
                        setDialogPeriodType('available');
                      }}
                      variant="secondary"
                      className="flex-1"
                    >
                      Annuller
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Day Periods Dialog */}
          {selectedDayPeriods && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setSelectedDayPeriods(null); setCalendarEditingIndex(null); setCalendarEditPeriod(null); }}>
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold mb-4">
                  Perioder for {formatDate(selectedDayPeriods.dateStr)}
                </h3>
                
                <div className="space-y-3">
                  {selectedDayPeriods.periods.map(({ period, index }) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${
                        period.available
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      {calendarEditingIndex === index && calendarEditPeriod ? (
                        // Edit mode
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={calendarEditPeriod.available}
                                onChange={() => setCalendarEditPeriod({ ...calendarEditPeriod, available: true })}
                                className="text-green-600"
                              />
                              <span className="text-sm font-medium text-gray-700">Tilgængelig</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={!calendarEditPeriod.available}
                                onChange={() => setCalendarEditPeriod({ ...calendarEditPeriod, available: false })}
                                className="text-red-600"
                              />
                              <span className="text-sm font-medium text-gray-700">Ikke tilgængelig</span>
                            </label>
                          </div>

                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Fra dato
                              </label>
                              <input
                                type="date"
                                value={calendarEditPeriod.startDate}
                                onChange={(e) =>
                                  setCalendarEditPeriod({ ...calendarEditPeriod, startDate: e.target.value })
                                }
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Til dato (valgfrit)
                              </label>
                              <input
                                type="date"
                                value={calendarEditPeriod.endDate || ''}
                                onChange={(e) =>
                                  setCalendarEditPeriod({ ...calendarEditPeriod, endDate: e.target.value || undefined })
                                }
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              type="button"
                              onClick={handleSaveEditFromCalendar}
                              variant="primary"
                              size="sm"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Gem
                            </Button>
                            <Button
                              type="button"
                              onClick={handleCancelEditFromCalendar}
                              variant="secondary"
                              size="sm"
                            >
                              <X className="h-4 w-4 mr-1" />
                              Annuller
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex items-start justify-between">
                          <div>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-2 ${
                                period.available
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {period.available ? 'Tilgængelig' : 'Ikke tilgængelig'}
                            </span>
                            <div className="text-sm text-gray-700">
                              <div><span className="font-medium">Fra:</span> {formatDate(period.startDate)}</div>
                              <div><span className="font-medium">Til:</span> {period.endDate ? formatDate(period.endDate) : 'Ubegrænset'}</div>
                            </div>
                          </div>
                          {!readOnly && (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleStartEditFromCalendar(index)}
                                className="text-gray-600 hover:text-gray-800"
                                title="Rediger"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeletePeriodFromCalendar(index)}
                                className="text-red-600 hover:text-red-800"
                                title="Slet periode"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="mt-4">
                  <Button
                    type="button"
                    onClick={() => { setSelectedDayPeriods(null); setCalendarEditingIndex(null); setCalendarEditPeriod(null); }}
                    variant="secondary"
                    className="w-full"
                  >
                    Luk
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Existing Periods */}
      <div className={`space-y-3 ${periods.length > 5 ? 'max-h-[500px] overflow-y-auto pr-2' : ''}`}>
        {periods.length === 0 && !isAddingPeriod && (
          <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600 mb-4">Ingen tilgængelighedsperioder endnu</p>
            <Button
              type="button"
              onClick={() => setIsAddingPeriod(true)}
              variant="secondary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Tilføj periode
            </Button>
          </div>
        )}

        {periods.map((period, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border-2 ${
              period.available
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}
          >
            {editingIndex === index && editPeriod ? (
              // Edit mode
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={editPeriod.available}
                      onChange={() => setEditPeriod({ ...editPeriod, available: true })}
                      className="text-green-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Tilgængelig</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={!editPeriod.available}
                      onChange={() => setEditPeriod({ ...editPeriod, available: false })}
                      className="text-red-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Ikke tilgængelig</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fra dato
                    </label>
                    <input
                      type="date"
                      value={editPeriod.startDate}
                      onChange={(e) =>
                        setEditPeriod({ ...editPeriod, startDate: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Til dato (valgfrit)
                    </label>
                    <input
                      type="date"
                      value={editPeriod.endDate || ''}
                      onChange={(e) =>
                        setEditPeriod({ ...editPeriod, endDate: e.target.value || undefined })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Ubegrænset"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={handleSaveEdit}
                    variant="primary"
                    size="sm"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Gem
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCancelEdit}
                    variant="secondary"
                    size="sm"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Annuller
                  </Button>
                </div>
              </div>
            ) : (
              // View mode
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        period.available
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {period.available ? 'Tilgængelig' : 'Ikke tilgængelig'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">Fra:</span> {formatDate(period.startDate)}
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">Til:</span>{' '}
                    {period.endDate ? formatDate(period.endDate) : 'Ubegrænset'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleStartEdit(index)}
                    className="text-gray-600 hover:text-gray-800"
                    title="Rediger"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeletePeriod(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add New Period Form */}
      {isAddingPeriod && (
        <div className="p-4 rounded-lg border-2 border-gray-300 bg-white space-y-3">
          <h4 className="font-medium text-gray-900">Tilføj ny periode</h4>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={newPeriod.available}
                onChange={() => setNewPeriod({ ...newPeriod, available: true })}
                className="text-green-600"
              />
              <span className="text-sm font-medium text-gray-700">Tilgængelig</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={!newPeriod.available}
                onChange={() => setNewPeriod({ ...newPeriod, available: false })}
                className="text-red-600"
              />
              <span className="text-sm font-medium text-gray-700">Ikke tilgængelig</span>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fra dato <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={newPeriod.startDate}
                onChange={(e) =>
                  setNewPeriod({ ...newPeriod, startDate: e.target.value })
                }
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Til dato (valgfrit)
              </label>
              <input
                type="date"
                value={newPeriod.endDate || ''}
                onChange={(e) =>
                  setNewPeriod({ ...newPeriod, endDate: e.target.value || undefined })
                }
                min={newPeriod.startDate || new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Ubegrænset"
              />
              <p className="text-xs text-gray-500 mt-1">
                Lad feltet være tomt for ubegrænset tilgængelighed
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleAddPeriod}
              variant="primary"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Tilføj periode
            </Button>
            <Button
              type="button"
              onClick={() => setIsAddingPeriod(false)}
              variant="secondary"
              size="sm"
            >
              Annuller
            </Button>
          </div>
        </div>
      )}

      {/* Add Button (when not adding) */}
      {!isAddingPeriod && periods.length > 0 && (
        <Button
          type="button"
          onClick={() => setIsAddingPeriod(true)}
          variant="secondary"
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Tilføj periode
        </Button>
      )}
        </>
      )}
    </div>
  );
};

export default ToolAvailabilityCalendar;
